/**
 * Routeur hash-based, sans dépendance externe. Routes en **anglais** par
 * convention Shopify-like (les libellés UI restent en français).
 *
 * Post-pivot e-commerce (cf. cahier-des-charges-ecommerce-v01.md §3.1) :
 * l'app est réduite au configurateur « Atelier ». Les routes communautaires
 * (community, patterns, account, admin) ont été supprimées, et le
 * multi-projets avec elles (Lot N4) : plus d'id de projet dans l'URL,
 * l'éditeur travaille sur un brouillon unique (cf. persistence/draft.ts).
 *
 * Routes :
 *  - `#/`       → home
 *  - `#/order`  → tunnel de commande sur-mesure (Lot P3)
 *  - `#/editor` → éditeur (brouillon unique)
 *
 * Tout hash inconnu (y compris les anciennes routes communautaires et les
 * anciens `#/editor/<id>`) retombe sur la route la plus proche.
 */

export type Route = 'home' | 'order' | 'editor' | 'admin';

export type RouterState = {
  route: Route;
};

export function parseHash(hash: string): RouterState {
  const h = hash.replace(/^#\/?/, '');
  if (h === '' || h === '/') return { route: 'home' };

  // `editor` et les anciens `editor/<id>` (ère multi-projets) → editor.
  if (h === 'editor' || h.startsWith('editor/')) return { route: 'editor' };

  if (h === 'order' || h.startsWith('order/')) return { route: 'order' };

  // Back-office admin (PIM) — protégé côté serveur (claim admin) ; ici on
  // route seulement, le composant gère l'accès (sign-in + garde isAdmin).
  if (h === 'admin' || h.startsWith('admin/')) return { route: 'admin' };

  return { route: 'home' };
}

function createRouter() {
  let state = $state<RouterState>(parseHash(window.location.hash));

  window.addEventListener('hashchange', () => {
    state = parseHash(window.location.hash);
  });

  function navigateTo(hash: string): void {
    if (window.location.hash !== hash) {
      window.location.hash = hash;
    }
  }

  return {
    get current(): Route {
      return state.route;
    },
    /** Navigue vers une route. */
    navigate(route: Route): void {
      if (route === 'home') return navigateTo('#/');
      navigateTo(`#/${route}`);
    },
  };
}

export const router = createRouter();
